import { useQuery } from '@tanstack/react-query'
import { type ReactNode } from 'react'

import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { fetchListingPrice } from '../lib/api.ts'

/**
 * Por quanto este anúncio sairia, e por quê (F111).
 *
 * ## Por que as faixas perdedoras aparecem
 *
 * Porque a pergunta não é "quanto custa" — é _"por que este anúncio está a
 * R$ 108,03 se eu vendo por R$ 70?"_. A resposta é que a faixa mais barata se
 * contradiz: a R$ 89,25 ela não vale mais, porque acima de R$ 79 o frete passa
 * a ser por conta do vendedor.
 *
 * Sem as faixas recusadas na tela, a única resposta possível seria "confia". É
 * o mesmo raciocínio de "pode publicar" mostrar o `limitedBy` ao lado do
 * número.
 */

const money = (value: string | null): string => {
  if (value === null) return '—'
  const [whole = '0', cents = '00'] = value.split('.')
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  return `R$ ${grouped},${cents.padEnd(2, '0').slice(0, 2)}`
}

const percent = (value: string): string => {
  const trimmed = value.replace(/0+$/, '').replace(/\.$/, '')
  return `${trimmed.replace('.', ',')}%`
}

export type ListingPriceDialogProps = {
  open: boolean
  channelId: string
  listingId: string
  productName: string
  onOpenChange: (open: boolean) => void
}

export const ListingPriceDialog = ({
  open,
  channelId,
  listingId,
  productName,
  onOpenChange,
}: ListingPriceDialogProps): ReactNode => {
  const quote = useQuery({
    queryKey: ['listing-price', channelId, listingId],
    queryFn: async ({ signal }) =>
      await fetchListingPrice(channelId, listingId, signal),
    enabled: open,
    /* A cotação de frete muda no tempo; não se guarda entre aberturas. */
    staleTime: 0,
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={`Cálculo do preço — ${productName}`}
        description="O preço de balcão com as taxas do canal dentro, e as faixas que foram consideradas."
        className="max-w-2xl"
      >
        {quote.isPending ? (
          <Spinner />
        ) : quote.isError ? (
          <p role="alert" className="text-(length:--text-sm) text-(--danger)">
            {quote.error instanceof Error
              ? quote.error.message
              : 'Não foi possível cotar este canal.'}
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-baseline gap-3">
              <span className="text-(length:--text-2xl) font-medium">
                {money(quote.data.price)}
              </span>
              {quote.data.ok ? (
                <Badge tone="success">Pode publicar</Badge>
              ) : (
                <Badge tone="danger">Não publica</Badge>
              )}
              {quote.data.pinnedToBand ? (
                <Badge tone="info">Preço fixado no piso da faixa</Badge>
              ) : null}
            </div>

            {quote.data.ok ? null : (
              <p
                role="alert"
                className="text-(length:--text-sm) text-(--danger)"
              >
                {quote.data.message}
              </p>
            )}

            <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-(length:--text-sm)">
              {(
                [
                  ['Preço de balcão', money(quote.data.basePrice)],
                  ['Comissão do canal', `− ${money(quote.data.commission)}`],
                  ['Custo fixo', `− ${money(quote.data.fixedFee)}`],
                  [
                    'Frete por conta da loja',
                    `− ${money(quote.data.sellerShipping)}`,
                  ],
                  ['Sobra na venda', money(quote.data.net)],
                  ['Custo médio', money(quote.data.averageCost)],
                  ['Margem mínima exige', money(quote.data.requiredNet)],
                ] as const
              ).map(([label, value]) => (
                <div key={label} className="contents">
                  <dt className="text-(--text-muted)">{label}</dt>
                  <dd className="text-right">{value}</dd>
                </div>
              ))}
            </dl>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Faixa</TableHeaderCell>
                    <TableHeaderCell>Comissão</TableHeaderCell>
                    <TableHeaderCell>Preço que daria</TableHeaderCell>
                    <TableHeaderCell>Vale?</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {quote.data.bands.map((band) => (
                    <TableRow key={band.label}>
                      <TableCell>{band.label}</TableCell>
                      <TableCell>{percent(band.commissionPercent)}</TableCell>
                      <TableCell>{money(band.price)}</TableCell>
                      <TableCell>
                        {band.chosen ? (
                          <Badge tone="success">Escolhida</Badge>
                        ) : band.consistent ? (
                          <Badge tone="neutral">Serviria</Badge>
                        ) : (
                          /*
                            A frase que responde "por que não é mais barato?".
                            "Não vale" sozinho mandaria a pessoa adivinhar.
                          */
                          <span className="text-(length:--text-xs) text-(--text-muted)">
                            A {money(band.price)} esta faixa não vale mais
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <p className="text-(length:--text-xs) text-(--text-muted)">
              Cotado em {new Date(quote.data.quotedAt).toLocaleString('pt-BR')}.
              O custo do frete depende da reputação da loja no canal e muda com
              o tempo.
            </p>
          </div>
        )}

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="ghost">
              Fechar
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
